/**
 * See: https://developers.google.com/web/fundamentals/instant-and-offline/web-storage/cache-api
 * The caches api allows a browser to cache a network response and match it against another request later.
 * It is complementary to the fetch api and can be also be used by service workers to offer a total offline experience.
 */

import stableStringify from 'json-stable-stringify';

import {debug} from 'common/report/util';
import {reporter} from 'common/report/error-reporting';

export const DEFAULT_TTL = 2 * 24 * 60 * 60; // cache for 2 days (yestday + today)

// cacheNamespace can be changed via setCacheNamespace()
const DEFAULT_CACHE_NAMESPACE = `fetch_cache_v1`;
let cacheNamespace = DEFAULT_CACHE_NAMESPACE;

function _getCacheRequest(url: string, options: RequestInit): Request {
  let cacheRequest = new Request(url, options);
  // Using `url.includes` here instead of parse via `new URL(...)` and checking
  // `pathname.startsWith` because the URL in the iso/browser tests come through as relative URLs.
  // It's very unlikely a false positive will happen due to the more permissive check.
  if (cacheRequest.method === `POST` && url.includes(`/api/2.0/`)) {
    // queryapi makes JSON GET requests as POST e.g insights
    // caches api cannot cache POST requests, so we cache them as GET
    const jsonBody = stableStringify(JSON.parse(options.body.toString()));
    cacheRequest = new Request(`${url}:${jsonBody}`, {...options, method: `GET`, body: null});
  }
  return cacheRequest;
}

/**
 * @param response response from cache
 * @param ttl time-to-live threshold in seconds for response to be marked as stale
 * @return true if response above ttl limit
 */
function _isResponseStale(response: Response, ttl: number): boolean {
  const dateHeader = response.headers.get(`date`);
  const aliveMs = dateHeader ? Date.now() - new Date(dateHeader).getTime() : DEFAULT_TTL;
  return aliveMs > ttl * 1000;
}

/** set a new cache namespace, e.g the current user id */
export function setCacheNamespace(cacheName: string) {
  cacheNamespace = cacheName;
}

/** Whether cache storage api is supported by browser */
export function canUseFetchCache(): boolean {
  return `caches` in window;
}

const cacheInstancesByName: Map<string, Promise<Cache>> = new Map();

/** Returns null if cache API is not supported or functional in the current browsing context */
export async function openBrowserCache(cacheName: string): Promise<Cache | null> {
  if (canUseFetchCache()) {
    try {
      // maintain a map of browser cache instances to only open each cache once per session
      if (!cacheInstancesByName.has(cacheName)) {
        cacheInstancesByName.set(cacheName, caches.open(cacheName));
      }
      return cacheInstancesByName.get(cacheName);
    } catch (err) {
      switch (err.name) {
        case `SecurityError`:
          // SecurityError - not recoverable, Firefox exposes window.caches but always throws this error in Private Mode or non-SSL contexts which is
          // non-standard behavior (https://bugzilla.mozilla.org/show_bug.cgi?id=1112134#c0)
          debug.warn(
            `Failed to open cache (likely because you are either using Firefox with a non-SSL URL or Private mode)`,
            err,
          );
          break;

        case `QuotaExceededError`:
          await purgeFetchCache();
          try {
            return await caches.open(cacheName);
          } catch (err) {
            /* give up */
            reporter.error(err, `Failed second attempt to open cache`);
          }
          break;

        default:
          throw err; // rethrow because we don't know what this is
      }
    }
  }

  return null;
}

/**
 * Make a fetch request to the cache. Similar api to fetch(url, opts) which fetches from network.
 * Returns null if response not found
 *
 * @param url url to fetch
 * @param options fetch options
 * @param ttl time-to-live in seconds
 */
export async function fromFetchCache(
  url: string,
  options: RequestInit,
  ttl = DEFAULT_TTL,
): Promise<{cachedResponse: Response | null; trackingDict: Obj}> {
  const time0 = new Date().getTime();
  const fetchCache = await openBrowserCache(cacheNamespace);
  const time1 = new Date().getTime();
  let returnValue = null;
  let time2, time3, time4;
  let cachedResponse = null;
  if (fetchCache) {
    const cacheRequest = _getCacheRequest(url, options);
    time2 = new Date().getTime();
    cachedResponse = await fetchCache.match(cacheRequest);
    time3 = new Date().getTime();
    if (cachedResponse && !_isResponseStale(cachedResponse, ttl)) {
      returnValue = cachedResponse;
    }
    time4 = new Date().getTime();
  }

  return {
    cachedResponse: returnValue,
    trackingDict: {
      cache_namespace: cacheNamespace,
      is_fetch_cache_present: Boolean(fetchCache),
      open_browser_cache_latency_ms: time1 - time0,
      ...(fetchCache
        ? {
            get_cache_request_latency_ms: time2 - time1,
            fetch_cache_match_latency_ms: time3 - time2,
            ...(cachedResponse ? {is_response_stale_latency_ms: time4 - time3} : {}),
          }
        : {}),
    },
  };
}

/**
 * @param url url that was fetched
 * @param options options passed to fetch()
 * @param response response returned by fetch() that will be cached with url+opts as the key
 */
// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export async function updateFetchCache(url: string, options: RequestInit, response: Response): Promise<void> {
  // we use response.clone because response body can only be read once
  // otherwise we get a 'body stream is locked' error
  const clonedResponse = response.clone(); // clone response first to ensure response body isn't read while awaiting openBrowserCache()

  const fetchCache = await openBrowserCache(cacheNamespace);
  if (fetchCache) {
    const cacheRequest = _getCacheRequest(url, options);

    if (cacheRequest.method === `GET` && clonedResponse.ok && clonedResponse.status === 200) {
      try {
        await fetchCache.put(cacheRequest, clonedResponse);
      } catch (err) {
        if (err.name === `QuotaExceededError`) {
          await purgeFetchCache();
          try {
            await fetchCache.put(cacheRequest, clonedResponse);
          } catch {
            /* give up */
            reporter.error(err, `Failed second attempt to update cache`);
          }
        } else {
          throw err;
        }
      }
    }
  }
}

export async function deleteFromFetchCache(url: string, options: RequestInit): Promise<boolean> {
  const fetchCache = await openBrowserCache(cacheNamespace);
  if (fetchCache) {
    const cacheRequest = _getCacheRequest(url, options);
    return fetchCache.delete(cacheRequest);
  }

  return false;
}

function getCachePathFromRequest(request: Request): string {
  const cachedUrl = new URL(request.url);
  // See _getCacheRequest for why we append ':' to the url
  return cachedUrl.pathname.split(`:`)[0];
}

/**
 * Delete all cache entries older than DEFAULT_TTL
 * Also delete all caches other than cacheNamespace.
 *
 * @param opts
 * @param opts.everything - if true, delete all caches, regardless if responses are under ttl or not
 * @param opts.ttl - delete entries above the ttl in seconds
 * @param opts.path - only purge entries which match the pathname
 */
// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export async function purgeFetchCache({everything = false, ttl = DEFAULT_TTL, path = null} = {}): Promise<void> {
  if (canUseFetchCache()) {
    cacheInstancesByName.clear();
    try {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        if (cacheName === cacheNamespace && !everything) {
          const fetchCache = await caches.open(cacheNamespace);
          const requests = await fetchCache.keys();
          for (const request of requests) {
            if (path !== null && getCachePathFromRequest(request) !== path) {
              continue;
            }
            const response = await fetchCache.match(request);
            if (response && _isResponseStale(response, ttl)) {
              // delete individual entries that are stale
              await fetchCache.delete(request);
            }
          }
        } else {
          // delete entire named cache which nukes all entries contained within it
          await caches.delete(cacheName);
        }
      }
    } catch (err) {
      /* no known recovery options */
      reporter.error(err, `Failed to purge cache`);
    }
  }
}

export async function purgeFetchCachePathsMatchingRegex(pathSearch: RegExp): Promise<void> {
  if (canUseFetchCache()) {
    try {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        const fetchCache = await caches.open(cacheName);
        const requests = await fetchCache.keys();
        for (const request of requests) {
          const path = getCachePathFromRequest(request);
          if (pathSearch.exec(path)) {
            await fetchCache.delete(request);
          }
        }
      }
    } catch (err) {
      /* no known recovery options */
      reporter.error(err, `Failed to purge cache`);
    }
  }
}

/**
 * NOTE: This is only used by standalone mode for dev via fetch_cache url param.
 * DO NOT use this function directly in prod.
 * It is aggressive and caches all fetch GET requests which is undesirable
 * It also does not update the ui once the network version is ready, so user may see stale data.
 */
export function installDevFetchCache(hydrate = true) {
  if (!canUseFetchCache()) {
    console.warn(`caches api not available, fetch cache not installed`);
    return;
  }

  if (window[`__origFetch`]) {
    // already installed
    return;
  }

  // clear out cache entries above TTL to save space
  purgeFetchCache();

  // fetch from network in background and update cache when it returns
  async function fetchAndHydrate(url: string, options: RequestInit) {
    const networkResponse = await window[`__origFetch`](url, options);
    updateFetchCache(url, options, networkResponse);
    return networkResponse;
  }

  window[`__origFetch`] = window.fetch;
  window.fetch = async function fetch(url: string, options: RequestInit) {
    let cachedResponse = null;
    let networkPromise = null;

    if (hydrate) {
      networkPromise = fetchAndHydrate(url, options);
    }

    try {
      cachedResponse = await fromFetchCache(url, options);
    } catch {
      // FetchCacheMiss, fallback to network version
      return networkPromise || fetchAndHydrate(url, options);
    }

    // otherwise return cached version
    return cachedResponse.cachedResponse;
  };
}

/* Filter out any search params we don't want to store as part of the cache key */
export function filterSearchParamsForCache(url: string): string {
  const splitUrl = url.split(`?`);
  const hasSearchParams = splitUrl.length > 1;
  const searchParams = hasSearchParams ? splitUrl[1] : ``;
  const searchParamsObject = new URLSearchParams(searchParams);
  searchParamsObject.delete(`use_query_cache`);
  if (hasSearchParams) {
    splitUrl[1] = searchParamsObject.toString();
  }

  return splitUrl.join(`?`);
}
